import React, { useEffect, useState } from "react";
import { Drawer, Loader } from "@mantine/core";
import moment from "moment";
import Icon from "./icons";
import { updateEvvRecord } from "../../../services/UserTracking";
import schedulerService from "../../../services/scheduler";
import { decrypt } from "../../../utils";
import "./SchedulerCreate.css";

const idOf = (o) => (typeof o === "object" ? o?._id || o?.id : o);

const clientName = (client) => {
  if (!client) return "";
  const f1 = typeof client.form1 === "string" ? safe(() => decrypt(client.form1)) : client.form1;
  if (f1?.firstName || f1?.lastName) return `${f1.firstName || ""} ${f1.lastName || ""}`.trim();
  if (typeof client.name === "string") return safe(() => decrypt(client.name)) || client.name;
  return client.name || "";
};
const serviceList = (record) => {
  const codes = Array.isArray(record?.services) && record.services.length ? record.services : record?.service ? [record.service] : [];
  const f1 = record?.client && (typeof record.client.form1 === "string" ? safe(() => decrypt(record.client.form1)) : record.client.form1);
  const titleFor = (code) => f1?.services?.find?.((s) => s.servicenumber === code)?.servicetitle;
  return codes.map((c) => ({ code: c, label: titleFor(c) ? `${titleFor(c)} (${c})` : c }));
};
function safe(fn) { try { return fn(); } catch { return null; } }

const fmtCoord = (loc) => (loc && loc.lat != null ? `${loc.lat}, ${loc.lng}` : "—");
const fmtDist = (d) => (d != null ? `${Number(d).toFixed(2)} mi` : "—");

// Scheduler-native EVV record panel (matches the sd-* drawer design). Shows the
// visit's verification detail (distance, GPS/address match, device), charting
// completion (Notes/MAR/Task), and lets an admin correct clock in/out + addresses.
export default function EvvRecordDrawer({ opened, record, onClose, onBack, onSaved }) {
  const [inDate, setInDate] = useState("");
  const [inTime, setInTime] = useState("");
  const [outDate, setOutDate] = useState("");
  const [outTime, setOutTime] = useState("");
  const [matched, setMatched] = useState("");
  const [serviceAddr, setServiceAddr] = useState("");
  const [busy, setBusy] = useState(false);
  const [charting, setCharting] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);

  useEffect(() => {
    if (!opened || !record) return;
    const ci = record.evvClockIn || {};
    const co = record.evvClockOut || {};
    // Fall back to the actual clock in/out times when no EVV timestamp was captured.
    const inTs = ci.timestamp || record.startTime;
    const outTs = co.timestamp || record.endTime;
    setInDate(inTs ? moment(inTs).format("YYYY-MM-DD") : "");
    setInTime(inTs ? moment(inTs).format("HH:mm") : "");
    setOutDate(outTs ? moment(outTs).format("YYYY-MM-DD") : "");
    setOutTime(outTs ? moment(outTs).format("HH:mm") : "");
    setMatched(ci.matchedAddress || co.matchedAddress || "");
    setServiceAddr(ci.serviceAddress || co.serviceAddress || "");
    setCharting(null);
    setChartLoading(true);
    schedulerService.getVisitCharting(idOf(record)).then((c) => setCharting(c)).finally(() => setChartLoading(false));
  }, [opened, record]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!record) return null;

  const ci = record.evvClockIn || {};
  const co = record.evvClockOut || {};
  const gpsOk = !!(ci.location && ci.location.lat != null);
  const isClient = record.type !== "facility";
  const cName = isClient ? clientName(record.client) : record.facility?.name || "";
  const services = serviceList(record);
  const dateStr = record.date?.date ? moment(record.date.date).format("dddd, MMM D, YYYY") : record.startTime ? moment(record.startTime).format("dddd, MMM D, YYYY") : "";

  const combine = (d, t) => (d ? moment(`${d} ${t || "00:00"}`, "YYYY-MM-DD HH:mm").toISOString() : null);

  const save = async () => {
    setBusy(true);
    const payload = { trackingId: idOf(record) };
    if (record.evvClockIn) payload.evvClockIn = { timestamp: combine(inDate, inTime), matchedAddress: matched, serviceAddress: serviceAddr };
    if (record.evvClockOut) payload.evvClockOut = { timestamp: combine(outDate, outTime), matchedAddress: matched, serviceAddress: serviceAddr };
    await updateEvvRecord(payload);
    setBusy(false);
    onSaved?.();
    onClose?.();
  };

  const kv = (label, value, mono) => (
    <div className="evvr-kv">
      <span className="evvr-k">{label}</span>
      <span className={`evvr-v ${mono ? "evvr-mono" : ""}`}>{value}</span>
    </div>
  );

  const chartChip = (label, done) => (
    <span className={`evvr-chip ${done ? "evvr-chip-on" : "evvr-chip-off"}`}>
      <Icon name={done ? "check" : "x"} size={12} /> {label}
    </span>
  );

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={520}
      padding={0}
      withCloseButton={false}
      title={null}
      classNames={{ content: "sdDrawerContent" }}
      styles={{ body: { height: "100%", padding: 0 } }}
      zIndex={530}
    >
      <div className="sd-root">
        <div className="sd-head">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {onBack ? <button type="button" className="sd-close" onClick={onBack} aria-label="Back"><Icon name="arrowLeft" size={16} /></button> : null}
            <div>
              <h3>EVV Record</h3>
              <div className="dh-sub">{dateStr}</div>
            </div>
          </div>
          <button type="button" className="sd-close" onClick={onClose} aria-label="Close"><Icon name="x" size={16} /></button>
        </div>

        <div className="sd-body sd-create">
          {/* Summary */}
          <div className="evvr-summary">
            <div>
              <div className="evvr-who-lbl">{isClient ? "Client" : "Facility"}</div>
              <div className="evvr-who">{cName || "—"}</div>
            </div>
            <div className="evvr-badges">
              {gpsOk ? <span className="evvr-badge on"><Icon name="check" size={11} /> GPS verified</span> : <span className="evvr-badge off">No GPS</span>}
              <span className={`evvr-badge ${record.evvEnforced ? "on" : "off"}`}>{record.evvEnforced ? "EVV enforced" : "Not enforced"}</span>
            </div>
          </div>

          {services.length > 0 ? (
            <div className="evvr-services">
              {services.map((s) => <span key={s.code} className="evvr-svc">{s.label}</span>)}
            </div>
          ) : null}

          {/* Charting completion */}
          <div className="evvr-section">
            <div className="evvr-sec-h">Charting completed</div>
            {chartLoading ? (
              <Loader size="xs" color="var(--app-primary)" />
            ) : charting && charting.hasClient ? (
              <div className="evvr-chips">
                {chartChip("Notes", charting.notes)}
                {chartChip("MAR", charting.mar)}
                {chartChip("Task", charting.task)}
              </div>
            ) : (
              <div className="evvr-muted">{charting && !charting.hasClient ? "Facility visit. No client charting." : "Charting status unavailable."}</div>
            )}
          </div>

          {/* Clock-In */}
          <div className="evvr-section">
            <div className="evvr-sec-h">Clock-In</div>
            <div className="frow">
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Date</label>
                <input className="inp" type="date" value={inDate} onChange={(e) => setInDate(e.target.value)} />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Time</label>
                <input className="inp" type="time" value={inTime} onChange={(e) => setInTime(e.target.value)} />
              </div>
            </div>
            <div className="evvr-kvs">
              {kv("Coordinates", fmtCoord(ci.location), true)}
              {kv("Distance to site", fmtDist(ci.distanceToSite))}
              {kv("Accuracy", ci.location?.accuracy != null ? `${Number(ci.location.accuracy).toFixed(1)}m` : "—")}
              {kv("Device", ci.deviceInfo?.deviceType || "—")}
              {kv("Location source", ci.deviceInfo?.locationSource || "—")}
            </div>
          </div>

          {/* Clock-Out */}
          <div className="evvr-section">
            <div className="evvr-sec-h">Clock-Out</div>
            <div className="frow">
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Date</label>
                <input className="inp" type="date" value={outDate} onChange={(e) => setOutDate(e.target.value)} />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Time</label>
                <input className="inp" type="time" value={outTime} onChange={(e) => setOutTime(e.target.value)} />
              </div>
            </div>
            <div className="evvr-kvs">
              {kv("Coordinates", fmtCoord(co.location), true)}
              {kv("Distance to site", fmtDist(co.distanceToSite))}
              {kv("Device", co.deviceInfo?.deviceType || "—")}
            </div>
          </div>

          {/* Address */}
          <div className="evvr-section">
            <div className="evvr-sec-h">Address</div>
            <div className="field">
              <label>Matched address</label>
              <input className="inp" value={matched} onChange={(e) => setMatched(e.target.value)} placeholder="—" />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Service address</label>
              <input className="inp" value={serviceAddr} onChange={(e) => setServiceAddr(e.target.value)} placeholder="—" />
            </div>
          </div>
        </div>

        <div className="sd-foot">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <span className="spacer" />
          <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={busy}>
            <Icon name="check" size={16} /> Save changes
          </button>
        </div>
      </div>
    </Drawer>
  );
}
